import type { BlogSlug } from "@listou/shared/constants";

import { creerListeDeNoelEnLigne } from "../content/blog/creer-liste-de-noel-en-ligne";
import { eviterDoublonsCadeauxNoel } from "../content/blog/eviter-doublons-cadeaux-noel";
import { ideesCadeauxSecretSanta } from "../content/blog/idees-cadeaux-secret-santa";
import { lettreAuPereNoel } from "../content/blog/lettre-au-pere-noel";
import { listeDeNaissanceGratuiteMultiEnseignes } from "../content/blog/liste-de-naissance-gratuite-multi-enseignes";
import { listeDeNaissanceQuandPartager } from "../content/blog/liste-de-naissance-quand-partager";
import { listeDeNoelEnfantIdeesParAge } from "../content/blog/liste-de-noel-enfant-idees-par-age";
import { meilleurSiteSecretSanta } from "../content/blog/meilleur-site-secret-santa";
import { meilleureListeAnniversaire } from "../content/blog/meilleure-liste-anniversaire";
import { meilleureListeDeNaissance } from "../content/blog/meilleure-liste-de-naissance";
import { meilleureListeDeNoel } from "../content/blog/meilleure-liste-de-noel";
import { pereNoelSecretEnFamille } from "../content/blog/pere-noel-secret-en-famille";
import { pereNoelSecretReglesBudget } from "../content/blog/pere-noel-secret-regles-budget";
import { queMettreListeDeNaissance } from "../content/blog/que-mettre-liste-de-naissance";
import { secretSantaEntreCollegues } from "../content/blog/secret-santa-entre-collegues";
import type { Cell } from "./comparisons";
import type { LandingOccasion } from "./occasion-pages";

/**
 * The blog, one content file per post in `content/blog/`.
 *
 * A post that names other services is comparative advertising, under the same rule as the
 * comparison pages (see `comparisons.ts`): every claim about a competitor says what their
 * own site says, `sources` lists where, and `checkedOn` is the day it was read. Listou is
 * our own service and the post says so. A guide that names no other service has no
 * `sources` and no `checkedOn`.
 */
export type BlogTable = {
  caption: string;
  columns: string[];
  rows: { name: string; cells: Cell[] }[];
};

export type BlogPost = {
  h1: string;
  seoTitle: string;
  seoDescription: string;
  /** ISO days. `updatedOn` moves when the content changes, not on a typo fix. */
  publishedOn: string;
  updatedOn: string;
  /** ISO day the competitors' sources were read. */
  checkedOn?: string;
  /** The answer, in two or three sentences: what a search snippet or an assistant quotes. */
  summary: string;
  /** The at-a-glance comparison, shown right after the summary. */
  table?: BlogTable;
  /** Markdown, from `##` down. */
  body: string;
  faq: [question: string, answer: string][];
  sources?: { label: string; url: string }[];
  /** Which product the closing call to action opens; a list by default. */
  cta?: "list" | "secret_santa";
  /**
   * The occasion the post is about. It dresses the page in that occasion's example list
   * (palette, stickers, the list itself in a phone) and preselects it in list creation.
   */
  occasion?: LandingOccasion;
  /** The call to action shown where `body` holds `INLINE_CTA`. */
  inlineCta?: BlogCard;
  /** Illustrated cards, shown where `body` holds `CARDS`. */
  cards?: BlogCard[];
  /** Printables, shown where `body` holds `DOWNLOADS`: files in `public/imprimables`. */
  downloads?: BlogDownload[];
  /** Example letters, drawn as handwritten pages where `body` holds `LETTERS`. */
  letters?: BlogLetter[];
};

/**
 * A letter as a child would hand it over: `paper` sets the sheet (blank for a drawing, the
 * French school ruling, plain lines), `stickers` stand in for its drawings.
 */
export type BlogLetter = {
  label: string;
  paper: "dessin" | "seyes" | "lignes";
  lines: string[];
  signature: string;
  stickers: string[];
};

/** `file` names both `<file>.pdf` and its preview `<file>.webp` (make-printables.ts). */
export type BlogDownload = { title: string; text: string; file: string };

/** `sticker` is a URL from the sticker library (`stickerUrl`). */
export type BlogCard = { title: string; text: string; sticker: string };

export const BLOG_POSTS: Record<BlogSlug, BlogPost> = {
  "meilleure-liste-de-naissance": meilleureListeDeNaissance,
  "meilleure-liste-anniversaire": meilleureListeAnniversaire,
  "meilleure-liste-de-noel": meilleureListeDeNoel,
  "meilleur-site-secret-santa": meilleurSiteSecretSanta,
  "pere-noel-secret-regles-budget": pereNoelSecretReglesBudget,
  "secret-santa-entre-collegues": secretSantaEntreCollegues,
  "idees-cadeaux-secret-santa": ideesCadeauxSecretSanta,
  "que-mettre-liste-de-naissance": queMettreListeDeNaissance,
  "liste-de-naissance-quand-partager": listeDeNaissanceQuandPartager,
  "pere-noel-secret-en-famille": pereNoelSecretEnFamille,
  "creer-liste-de-noel-en-ligne": creerListeDeNoelEnLigne,
  "eviter-doublons-cadeaux-noel": eviterDoublonsCadeauxNoel,
  "liste-de-noel-enfant-idees-par-age": listeDeNoelEnfantIdeesParAge,
  "lettre-au-pere-noel": lettreAuPereNoel,
  "liste-de-naissance-gratuite-multi-enseignes": listeDeNaissanceGratuiteMultiEnseignes,
};

/** schema.org Article and FAQPage in one graph: the questions as the page shows them. */
export const blogJsonLd = (post: BlogPost) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      headline: post.h1,
      description: post.seoDescription,
      datePublished: post.publishedOn,
      dateModified: post.updatedOn,
      inLanguage: "fr-FR",
      author: { "@type": "Organization", name: "Listou" },
      publisher: { "@type": "Organization", name: "Listou" },
    },
    {
      "@type": "FAQPage",
      mainEntity: post.faq.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
      })),
    },
  ],
});
